import React from 'react';
import { Box, Typography, Button, Card, CardContent } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import HomeIcon from '@mui/icons-material/Home';
import { Link } from 'react-router-dom';

class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, error: null, errorInfo: null };
    }

    static getDerivedStateFromError(error) {
        return { hasError: true };
    }

    componentDidCatch(error, errorInfo) {
        this.setState({
            error: error,
            errorInfo: errorInfo,
        });

        // Log error to monitoring service in production
        if (process.env.NODE_ENV === 'production') {
            // You can integrate with services like Sentry, LogRocket, etc.
            console.error('Error caught by boundary:', error, errorInfo);
        }
    }

    handleRefresh = () => {
        window.location.reload();
    };

    handleGoHome = () => {
        this.setState({ hasError: false, error: null, errorInfo: null });
    };

    render() {
        if (this.state.hasError) {
            return (
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minHeight: '100vh',
                        backgroundColor: '#f5f5f5',
                        p: 3,
                    }}
                >
                    <Card
                        sx={{
                            maxWidth: 500,
                            width: '100%',
                            textAlign: 'center',
                            borderRadius: 3,
                            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                        }}
                    >
                        <CardContent sx={{ p: 4 }}>
                            <ErrorOutlineIcon
                                sx={{
                                    fontSize: 80,
                                    color: '#f44336',
                                    mb: 2,
                                }}
                            />

                            <Typography variant="h5" gutterBottom fontWeight={600}>
                                Oops! Something went wrong
                            </Typography>

                            <Typography
                                variant="body1"
                                color="text.secondary"
                                sx={{ mb: 3 }}
                            >
                                We're sorry, but something unexpected happened. Please try refreshing the page or go back to the home page.
                            </Typography>

                            {process.env.NODE_ENV !== 'production' && this.state.error && (
                                <Box
                                    sx={{
                                        backgroundColor: '#fff3f3',
                                        borderRadius: 2,
                                        p: 2,
                                        mb: 3,
                                        textAlign: 'left',
                                        overflow: 'auto',
                                        maxHeight: 200,
                                    }}
                                >
                                    <Typography
                                        variant="caption"
                                        component="pre"
                                        sx={{
                                            fontFamily: 'monospace',
                                            fontSize: '0.75rem',
                                            color: '#d32f2f',
                                            whiteSpace: 'pre-wrap',
                                            wordBreak: 'break-word',
                                        }}
                                    >
                                        {this.state.error.toString()}
                                        {this.state.errorInfo?.componentStack}
                                    </Typography>
                                </Box>
                            )}

                            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                                <Button
                                    variant="outlined"
                                    startIcon={<RefreshIcon />}
                                    onClick={this.handleRefresh}
                                    sx={{
                                        borderColor: '#0db2ad',
                                        color: '#0db2ad',
                                        '&:hover': {
                                            borderColor: '#005d6d',
                                            backgroundColor: 'rgba(13, 178, 173, 0.04)',
                                        },
                                    }}
                                >
                                    Refresh Page
                                </Button>

                                <Button
                                    component={Link}
                                    to="/"
                                    variant="contained"
                                    startIcon={<HomeIcon />}
                                    onClick={this.handleGoHome}
                                    sx={{
                                        backgroundColor: '#0db2ad',
                                        '&:hover': {
                                            backgroundColor: '#005d6d',
                                        },
                                    }}
                                >
                                    Go Home
                                </Button>
                            </Box>
                        </CardContent>
                    </Card>
                </Box>
            );
        }

        return this.props.children;
    }
}

export default ErrorBoundary;
